import { z } from 'zod';
import { uuidSchema, currencyCodeSchema, positiveDecimalSchema } from './common';

/** Bir API çağrısında en fazla 200 transaction üzerinde toplu işlem yapılabilir. */
const BULK_MAX = 200;

export const bulkDeleteTransactionsSchema = z.object({
  ids: z.array(uuidSchema).min(1).max(BULK_MAX),
});
export type BulkDeleteTransactionsInput = z.infer<typeof bulkDeleteTransactionsSchema>;

export const bulkTagTransactionsSchema = z.object({
  ids: z.array(uuidSchema).min(1).max(BULK_MAX),
  tagIds: z.array(uuidSchema).min(1).max(20),
});
export type BulkTagTransactionsInput = z.infer<typeof bulkTagTransactionsSchema>;

/** 💳 Toplu para hesabı atama. moneyAccountId null → hesap bağını kaldırır. */
export const bulkSetMoneyAccountSchema = z.object({
  ids: z.array(uuidSchema).min(1).max(BULK_MAX),
  moneyAccountId: uuidSchema.nullable(),
});
export type BulkSetMoneyAccountInput = z.infer<typeof bulkSetMoneyAccountSchema>;

/** 📂 Toplu kategori (kart) taşıma — seçili işlemleri targetCardId'ye taşır. */
export const bulkMoveCardSchema = z.object({
  ids: z.array(uuidSchema).min(1).max(BULK_MAX),
  targetCardId: uuidSchema,
});
export type BulkMoveCardInput = z.infer<typeof bulkMoveCardSchema>;

/**
 * 🧾 Toplu işlemlerde (hesap atama / kategori taşıma) bir kaydın ATLANMA nedeni. Web "0 işlem
 * güncellendi" yerine nedeni gösterebilsin diye sayaç olarak döner:
 *  - locked: kilitli dönem (önce kilit açılmalı)
 *  - carryover: devir bakiyesi kaydı / devir kartı (sistem kaydı)
 *  - settlement: Otomatik Banka Takası bağlı işlem (tek tek düzenlenmeli)
 *  - currency: hedef hesabın dövizine uymayan işlem
 *  - splitPart: bölüşüm parçası (ana işlemden yönetilir)
 *  - error: bulunamadı / yetki yok / beklenmeyen hata
 */
export const bulkSkipReasonSchema = z.enum([
  'locked',
  'carryover',
  'settlement',
  'currency',
  'splitPart',
  'error',
]);
export type BulkSkipReason = z.infer<typeof bulkSkipReasonSchema>;
export type BulkSkippedReasons = Partial<Record<BulkSkipReason, number>>;

/** POST /transactions/bulk/set-account yanıtı. */
export interface BulkSetMoneyAccountResult {
  updated: number;
  skipped: number;
  skippedReasons: BulkSkippedReasons;
}

/** POST /transactions/bulk/move-card yanıtı. */
export interface BulkMoveCardResult {
  moved: number;
  skipped: number;
  skippedReasons: BulkSkippedReasons;
}

/**
 * 🧬 Yinelenen kayıt bulucu (GET …/periods/:y/:m/duplicates) — bir kopyanın KÖKENİ.
 * Teşhis betiğindeki (`scripts/rollout-budget-fix/duplicates-diagnose.sql`) sınıflarla aynı sıra:
 *  - bank: banka/sağlayıcı importu (meta.externalId); bankMerged: elle girilmiş kayıt banka
 *    hareketiyle birleştirilmiş (meta.mergedFromExternal);
 *  - csv: CSV içe aktarma; recurringBackfill: eksik tekrar onarımı; recurringCopy: tekrar kopyası
 *    (source_transaction_id); carryCopy: sonraki aya taşıma kopyası (carried_from_transaction_id);
 *  - paymentPlan: ödeme planı taksiti; chat: sohbetle oluşturulan; otherSource: başka bir kaynak
 *    (meta._audit.source); manual: elle girilen / bilinmeyen.
 */
export const duplicateOriginSchema = z.enum([
  'bank',
  'bankMerged',
  'csv',
  'recurringBackfill',
  'recurringCopy',
  'carryCopy',
  'paymentPlan',
  'chat',
  'otherSource',
  'manual',
]);
export type DuplicateOrigin = z.infer<typeof duplicateOriginSchema>;

/** Tek istekte çözülebilecek en fazla grup / gruptan silinecek kayıt / toplam silinecek kayıt. */
const DUPLICATE_GROUPS_MAX = 100;
const DUPLICATE_REMOVES_PER_GROUP_MAX = 50;
const DUPLICATE_REMOVES_TOTAL_MAX = 500;

/**
 * 🧹 POST …/periods/:y/:m/duplicates/resolve gövdesi: her grup için korunacak kayıt ve silinecek
 * kopyalar. Sunucu her id'yi yeniden doğrular (canlı, aynı dönem, aynı grup imzası) — keyfi id
 * silinmez. Aynı id iki kez (ya da hem korunacak hem silinecek) gönderilemez.
 */
export const resolveDuplicatesSchema = z
  .object({
    groups: z
      .array(
        z.object({
          keepId: uuidSchema,
          removeIds: z.array(uuidSchema).min(1).max(DUPLICATE_REMOVES_PER_GROUP_MAX),
        }),
      )
      .min(1)
      .max(DUPLICATE_GROUPS_MAX),
  })
  .superRefine((body, ctx) => {
    const seen = new Set<string>();
    let total = 0;
    body.groups.forEach((group, gi) => {
      for (const id of [group.keepId, ...group.removeIds]) {
        if (seen.has(id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Aynı işlem birden çok kez gönderilemez',
            path: ['groups', gi],
          });
          return;
        }
        seen.add(id);
      }
      total += group.removeIds.length;
    });
    if (total > DUPLICATE_REMOVES_TOTAL_MAX) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Tek istekte en fazla ${DUPLICATE_REMOVES_TOTAL_MAX} kayıt silinebilir`,
        path: ['groups'],
      });
    }
  });
export type ResolveDuplicatesInput = z.infer<typeof resolveDuplicatesSchema>;

/**
 * Çözümde bir kopyanın ATLANMA nedeni (sayaç olarak döner):
 *  - notDuplicate: kayıt artık canlı değil, bu dönemde değil ya da korunan kayıtla aynı grupta değil;
 *  - splitPart: bölüşüm parçası ya da bölüşüm parçaları olan ana işlem (ana işlemden yönetilir);
 *  - hasSplits: hane bölüşümü (expense_splits) var — paylar kaybolmasın diye elle çözülmeli;
 *  - error: beklenmeyen hata.
 */
export const duplicateSkipReasonSchema = z.enum([
  'notDuplicate',
  'splitPart',
  'hasSplits',
  'error',
]);
export type DuplicateSkipReason = z.infer<typeof duplicateSkipReasonSchema>;
export type DuplicateSkippedReasons = Partial<Record<DuplicateSkipReason, number>>;

/** POST …/duplicates/resolve yanıtı. */
export interface ResolveDuplicatesResult {
  removed: number;
  skipped: number;
  skippedReasons: DuplicateSkippedReasons;
}

const optionalDateSchema = z.preprocess(
  (v) => (v === '' || v === null || v === undefined ? undefined : v),
  z.string().date().optional(),
);

const optionalTimeSchema = z.preprocess(
  (v) => (v === '' || v === null || v === undefined ? undefined : v),
  z
    .string()
    .regex(/^\d{2}:\d{2}(:\d{2})?$/)
    .optional(),
);

export const transactionSubtypeSchema = z.enum(['load', 'spend']);
export type TransactionSubtype = z.infer<typeof transactionSubtypeSchema>;

/**
 * 🏠 Reports scope — couples/aile bütçesi ayrımı için.
 *  - all (default): tüm transactions (kişisel + ortak)
 *  - personal: householdGroupId IS NULL
 *  - household: householdGroupId IS NOT NULL
 */
export const reportScopeSchema = z.enum(['all', 'personal', 'household']).default('all');
export type ReportScope = z.infer<typeof reportScopeSchema>;

/**
 * Polymorphic split payment: ya bir benefit/budget kart'ına (paymentCardId) ya da
 * bir para hesabına (moneyAccountId) yönlendirilir. Tam olarak biri zorunludur.
 *  - paymentCardId: benefit kart üzerinde mirror transaction oluşturulur (eski davranış).
 *  - moneyAccountId: ek bir kayıt; sadece bookkeeping (mirror yok). Bir gideri
 *    birden fazla hesaptan ödemek için kullanılır.
 */
export const splitPaymentSchema = z
  .object({
    paymentCardId: uuidSchema.optional(),
    moneyAccountId: uuidSchema.optional(),
    amount: positiveDecimalSchema,
    note: z.string().max(200).optional(),
  })
  .refine((d) => Boolean(d.paymentCardId) !== Boolean(d.moneyAccountId), {
    message: 'paymentCardId VEYA moneyAccountId, sadece biri belirtilmelidir',
    path: ['paymentCardId'],
  });
export type SplitPaymentInput = z.infer<typeof splitPaymentSchema>;

export const transactionEntrySchema = z.object({
  amount: positiveDecimalSchema,
  // ⚠️ Para birimi OPSİYONEL — sabit 'TRY' default'u YOK. Verilmezse TransactionsService
  // merkezî olarak `entry.currency ?? getBaseCurrency()` ile çözer (Avrupa-öncelik: base EUR).
  // Hardcoded 'TRY' default'u, partial update'te tutar-yalnız düzenlemede para birimini
  // sessizce TRY'ye çeviriyordu (sohbet edit clobber bug'ı) — bu yüzden kaldırıldı.
  currency: currencyCodeSchema.optional(),
  exchangeRate: z.string().optional(),
  baseCurrency: currencyCodeSchema.optional(),
});
export type TransactionEntryInput = z.infer<typeof transactionEntrySchema>;

/** Update'te entry alanları (amount dahil) opsiyonel — yalnız para birimi ya da yalnız tutar değiştirilebilir. */
export const updateTransactionEntrySchema = transactionEntrySchema.partial();
export type UpdateTransactionEntryInput = z.infer<typeof updateTransactionEntrySchema>;

export const createTransactionSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(1000).optional(),
  subtype: transactionSubtypeSchema,

  entries: z.array(transactionEntrySchema).optional().default([]),

  date: optionalDateSchema,
  time: optionalTimeSchema,
  dueDate: optionalDateSchema,
  // ⏰ Tek-seferlik zamanlı oto-onay: true + dueDate verilirse işlem onaylanmamış
  // oluşturulur, AutoConfirmCron `autoConfirmAt = dueDate` geldiğinde onaylar
  // (autoRepeat'ten bağımsız). Sohbette "2 gün sonra otomatik çekilecek" akışı.
  autoConfirmOnDue: z.boolean().optional().default(false),
  // ✅ Oluştururken onayla: entry doğrudan onaylı (isConfirmed=true, confirmedAmount=amount)
  // doğar. Sohbette "ödedim" deyip yeni işlem yaratıldığında ve ödeme planı (échéancier)
  // taksitlerinde "ödenmiş" satırlar için kullanılır. confirm() çağrısına gerek kalmaz.
  confirmOnCreate: z.boolean().optional().default(false),
  appointmentDate: optionalDateSchema,
  appointmentTime: optionalTimeSchema,

  autoRepeat: z.boolean().optional().default(false),
  repeatMonths: z.number().int().min(0).optional(),
  repeatInterval: z.number().int().min(1).max(24).optional(),

  isInstallment: z.boolean().optional().default(false),
  totalInstallments: z.number().int().min(2).optional(),
  installmentNumber: z.number().int().min(1).optional(),

  // 🗓️ Ödeme planı (échéancier) bağı — bu işlem bir PaymentPlan'ın taksitidir.
  // Set edilirse tembel InstallmentRolloverService bu taksite dokunmaz (plan kendi
  // taksitlerini eager yaratır). Genelde PaymentPlansService tarafından doldurulur.
  paymentPlanId: uuidSchema.optional(),

  parentTransactionId: uuidSchema.optional(),

  /** Bu tx bir TransactionTemplate'ten oluşturulduysa template id. */
  transactionTemplateId: uuidSchema.optional(),

  tagIds: z.array(uuidSchema).optional().default([]),

  splitPayments: z.array(splitPaymentSchema).optional().default([]),

  meta: z.record(z.unknown()).optional(),

  moneyAccountId: uuidSchema.optional(),

  // 🏠 Aile bütçesi (household group) bağı. Set edilirse işlem ortak gider
  // sayılır; HouseholdSplitService.calculate sonucu splitPayments otomatik
  // doldurulabilir (caller responsibility — frontend toggle akışı).
  // nullable: update partial'da explicit null ile temizleme.
  householdGroupId: uuidSchema.nullable().optional(),

  // Senaryo 1 (PayPal): settlementAccountId + settlementAmount? + settlementDate?
  //   → transfer otomatik oluşturulur: settlementAccount → moneyAccount
  //   nullable: update'te explicit null ile takası kaldırma.
  settlementAccountId: uuidSchema.nullable().optional(),
  settlementAmount: positiveDecimalSchema.optional(),
  settlementDate: optionalDateSchema,

  // 🇪🇺 VAT / MwSt / IVA — freelancer & business expense tracking
  isBusinessExpense: z.boolean().optional().default(false),
  vatRate: z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? undefined : v),
    z.coerce.number().min(0).max(100).optional(),
  ),
  vatAmount: positiveDecimalSchema.optional(),
});
export type CreateTransactionInput = z.infer<typeof createTransactionSchema>;

// Update: tüm üst-düzey alanlar opsiyonel + entries elemanları da partial (amount opsiyonel) →
// "sadece para birimini değiştir" ya da "sadece tutarı değiştir" mümkün; create katı kalır.
export const updateTransactionSchema = createTransactionSchema.partial().extend({
  entries: z.array(updateTransactionEntrySchema).optional(),
  /** 💳 undefined → dokunma; uuid → ata/değiştir; null → hesap bağını kaldır. */
  moneyAccountId: uuidSchema.nullable().optional(),
});
export type UpdateTransactionInput = z.infer<typeof updateTransactionSchema>;

/**
 * 🔐 HTTP'den gelen işlem meta'sı — ALLOWLIST. Meta'daki diğer anahtarlar sunucuya aittir ve
 * güvenilir sistem işareti olarak okunur: devir (isBalanceCarryover/isBenefitCarryover), bölüşüm
 * (splitOriginId/splitPaymentId), import/dedup (externalId, csvHash, possibleDuplicateOf…), sohbet
 * izi (_audit), fonlama (fundedFrom)… Kullanıcı bunları yazabilirse kalıcı banka↔bütçe kayması
 * üretir, kendi işlemini kilitler ya da sonraki banka importunu tekilleştirmede yutturur.
 *
 * Yalnız istemcilerin (web işlem diyaloğu) gerçekten gönderdiği UI anahtarları kabul edilir;
 * bilinmeyen anahtarlar sessizce DÜŞER (`z.object` varsayılanı strip). Sohbet ve ödeme planı
 * `_audit`'i servis içinden (createTransactionSchema ile) geçirir; onlar bu şemadan geçmez.
 */
export const userTransactionMetaSchema = z.object({
  /** Yapılacak (todo) önceliği. */
  priority: z.enum(['urgent', 'high', 'medium', 'low', 'none']).optional(),
  /** Randevu hatırlatıcısı: kaç dakika önce (en çok 30 gün). */
  reminderMinutes: z.number().int().min(0).max(43_200).optional(),
});
export type UserTransactionMeta = z.infer<typeof userTransactionMetaSchema>;

/** Kullanıcıya ait (HTTP'den yazılabilir) meta anahtarları — update'te birleştirme için. */
export const USER_TRANSACTION_META_KEYS = Object.keys(
  userTransactionMetaSchema.shape,
) as ReadonlyArray<keyof UserTransactionMeta>;

/** POST /cards/:cardId/transactions gövdesi: create şeması + allowlist'li meta. */
export const createTransactionRequestSchema = createTransactionSchema.extend({
  meta: userTransactionMetaSchema.optional(),
});
export type CreateTransactionRequest = z.infer<typeof createTransactionRequestSchema>;

/** PATCH /transactions/:id gövdesi: update şeması + allowlist'li meta. */
export const updateTransactionRequestSchema = updateTransactionSchema.extend({
  meta: userTransactionMetaSchema.optional(),
});
export type UpdateTransactionRequest = z.infer<typeof updateTransactionRequestSchema>;

export const confirmTransactionSchema = z.object({
  entryId: uuidSchema.optional(),
  confirmedAmount: positiveDecimalSchema.optional(),
  note: z.string().max(500).optional(),
  /**
   * 🔁 Taşınmış orijinali BURADA onayla: kopya zinciri dokunulmamışsa önce taşıma geri alınır
   * (kopyalar silinir, işaret temizlenir), ardından işlem kendi ayında onaylanır. Geri alınamıyorsa
   * 400 TRANSACTION_CARRY_NOT_REVERTIBLE. Bayraksız taşınmış orijinal yine 400 TRANSACTION_CARRIED.
   */
  pullBackCarry: z.boolean().optional(),
});
export type ConfirmTransactionInput = z.infer<typeof confirmTransactionSchema>;

// ─── 🔁 Taşıma (carry) bağı: tür, dokunulmuşluk, geri alma ─────────────────────────────────

/**
 * Taşıma bağının türü (kopyadan bakılarak):
 *  - manual: kullanıcı "Gelecek aya taşı" ile taşıdı (meta.carryKind ya da etkinlik günlüğü /
 *    `transaction_carried` bildirimi kanıtı);
 *  - auto: sistemin onaysız kalem taşıması (ay sonu kapanışı; eski kodda ay bitmeden — erken);
 *  - appointment: randevu tarihine göre taşıma;
 *  - scheduled: kilitli aya planlanmış işlemin aktarımı;
 *  - unknown: kanıt yetersiz (otomatik onarılmaz; kullanıcı geri alabilir).
 */
export const carryKindSchema = z.enum(['manual', 'auto', 'appointment', 'scheduled', 'unknown']);
export type CarryKind = z.infer<typeof carryKindSchema>;

/** Kopyanın "dokunulmuş" sayılma nedeni (dokunulmuş kopya silinmez; onay kopyadan yapılır). */
export const carryTouchReasonSchema = z.enum([
  'copy_confirmed',
  'copy_entries_changed',
  'copy_edited',
  'copy_updated',
  'copy_tags_changed',
  'copy_splits_changed',
  'copy_payments_changed',
  'copy_card_deleted',
  'copy_bank_linked',
  'copy_has_dependants',
  'copy_user_subtasks',
]);
export type CarryTouchReason = z.infer<typeof carryTouchReasonSchema>;

/** Taşıma neden geri alınamaz? */
export const carryPullBackBlockReasonSchema = z.enum([
  'touched',
  'locked',
  'source_ended',
  'scheduled',
  'origin_inactive',
  'link_mismatch',
  'chain_too_long',
]);
export type CarryPullBackBlockReason = z.infer<typeof carryPullBackBlockReasonSchema>;

/** Kullanıcının "Taşımayı geri al" / "Burada onayla" eylemi yapılabilir mi? */
export const carryPullBackSchema = z.object({
  allowed: z.boolean(),
  /** allowed=true ise null. */
  reason: carryPullBackBlockReasonSchema.nullable(),
  /** allowed=true ise []. */
  touchReasons: z.array(carryTouchReasonSchema),
  /** Engelleyen kopya (web "Kopyayı aç" bağlantısı için); yoksa null. */
  blockedCopyId: uuidSchema.nullable(),
});
export type CarryPullBack = z.infer<typeof carryPullBackSchema>;

export const carryPeriodRefSchema = z.object({
  periodId: uuidSchema,
  budgetId: uuidSchema,
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
});
export type CarryPeriodRef = z.infer<typeof carryPeriodRefSchema>;

/**
 * Taşınmış orijinalin hedefi. Yalnız `carriedCopyAlive === true` iken dolu; aksi halde null.
 * year/month/periodId CANLI son halkanın (liveCopyId) dönemidir. Doğrudan kopya ölü olsa da kalem
 * ötesindeki canlı torunda ya da orijinalin canlı öksüz kopyasındaysa doludur (liveCopyId o kopya).
 */
export const carriedToInfoSchema = carryPeriodRefSchema.extend({
  /** İlk halka (= carriedToTransactionId); silinmiş/iptal olabilir. */
  copyId: uuidSchema,
  /** Kalemin yaşadığı CANLI son kopya; "Kopyayı aç" buraya gider. */
  liveCopyId: uuidSchema,
  /** Canlı kopya sayısı (geri alınırsa silinecek kopya sayısı). */
  chainLength: z.number().int().min(1),
  /** İlk halkanın türü. */
  kind: carryKindSchema,
  pullBack: carryPullBackSchema,
});
export type CarriedToInfo = z.infer<typeof carriedToInfoSchema>;

/** Köken zincirindeki en yakın canlı ve onaylı ata (dönemi + işlem). */
export const carryConfirmedOriginSchema = carryPeriodRefSchema.extend({
  transactionId: uuidSchema,
});
export type CarryConfirmedOrigin = z.infer<typeof carryConfirmedOriginSchema>;

/**
 * Bu işlem bir taşıma kopyasıysa kökeni: year/month/periodId/transactionId DOĞRUDAN kökenin
 * ("… döneminden taşındı", "Orijinali aç"). Köken satırı silinmişse null — ancak köken zincirinde
 * canlı ve onaylı bir ata varsa o ata gösterilir (`originConfirmed: true`, `linked: false`).
 */
export const carriedFromInfoSchema = carryPeriodRefSchema.extend({
  transactionId: uuidSchema,
  /**
   * Köken — ya da köken zincirindeki (`carried_from` boyunca) bir ata — canlı ve onaylı → bu kopya
   * onaylanamaz ve ay sonunda taşınmaz (ödeme iki kez sayılırdı). `confirmedOrigin !== null` ile aynı.
   */
  originConfirmed: z.boolean(),
  /**
   * Ödemenin yapıldığı en yakın canlı onaylı ata (doğrudan köken ya da zincirde daha eski bir halka);
   * yoksa null. "Fazla kopya" uyarısının ayı buradan gelir — API 400
   * TRANSACTION_CARRY_ORIGIN_CONFIRMED'in `originId`/`origin`'i ile aynıdır (Ağustos onaylı → Eylül
   * hayalet → Ekim kopyası: carriedFrom Eylül, confirmedOrigin Ağustos).
   */
  confirmedOrigin: carryConfirmedOriginSchema.nullable(),
  /** köken.carriedToTransactionId === bu kopya. */
  linked: z.boolean(),
});
export type CarriedFromInfo = z.infer<typeof carriedFromInfoSchema>;

/** POST /transactions/:id/undo-carry yanıtı. */
export const undoCarryResultSchema = z.object({
  /** Güncel işlem (detay yanıtıyla aynı şekil). */
  transaction: z.record(z.unknown()),
  revertedCopyIds: z.array(uuidSchema),
  affectedPeriodIds: z.array(uuidSchema),
});
export type UndoCarryResult = z.infer<typeof undoCarryResultSchema>;

/** Taşıma ile ilgili hata kodları (`details.code`). */
export const TRANSACTION_CARRY_ERROR_CODES = {
  carried: 'TRANSACTION_CARRIED',
  notRevertible: 'TRANSACTION_CARRY_NOT_REVERTIBLE',
  originConfirmed: 'TRANSACTION_CARRY_ORIGIN_CONFIRMED',
} as const;

// 🇪🇺 VAT report query — quarter veya custom date range
export const vatReportQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
  quarter: z.coerce.number().int().min(1).max(4).optional(),
  from: z.string().date().optional(),
  to: z.string().date().optional(),
});
export type VatReportQuery = z.infer<typeof vatReportQuerySchema>;

export const cancelTransactionSchema = z.object({
  reason: z.string().min(1).max(500),
});
export type CancelTransactionInput = z.infer<typeof cancelTransactionSchema>;

export const refundTransactionSchema = z.object({
  amount: positiveDecimalSchema.optional(),
  reason: z.string().min(1).max(500),
  /**
   * İadenin para hesaplarına/kartlara dağılımı. Verilmezse orijinal işlemin
   * split dağılımı aynen kopyalanır (para çıktığı hedeflere geri döner).
   * Boş dizi [] → dağılım yok, iade tamamen ana hesaba/karta döner.
   */
  splitPayments: z.array(splitPaymentSchema).optional(),
});
export type RefundTransactionInput = z.infer<typeof refundTransactionSchema>;

export const carryToNextPeriodSchema = z.object({
  targetYear: z.number().int().optional(),
  targetMonth: z.number().int().min(1).max(12).optional(),
  note: z.string().max(500).optional(),
});
export type CarryToNextPeriodInput = z.infer<typeof carryToNextPeriodSchema>;

export const moveTransactionSchema = z.object({
  targetCardId: uuidSchema,
  note: z.string().max(500).optional(),
});
export type MoveTransactionInput = z.infer<typeof moveTransactionSchema>;

export const copyTransactionSchema = z.object({
  targetCardId: uuidSchema,
  copyEntries: z.boolean().optional().default(true),
  copyTags: z.boolean().optional().default(true),
  copySplitPayments: z.boolean().optional().default(false),
  resetConfirmation: z.boolean().optional().default(true),
  note: z.string().max(500).optional(),
});
export type CopyTransactionInput = z.infer<typeof copyTransactionSchema>;

export const reorderTransactionsSchema = z.object({
  txIds: z.array(uuidSchema).min(1).max(500),
});
export type ReorderTransactionsInput = z.infer<typeof reorderTransactionsSchema>;
